import { z } from 'zod';

/**
 * Response row from GET /sessions/practitioners?clientId=.
 * This is the ONLY practitioner-discovery endpoint clients can call — they
 * lack `practitioner:read`, so /practitioners/:id + /practitioners/:id/schedule
 * return 403. The list is pre-filtered to the practitioners assigned at the
 * client's location. Fields are kept intentionally permissive (nullish) so
 * small backend response variations don't break the schema parse.
 */
export const assignedPractitionerSchema = z.object({
  id: z.string(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  name: z.string().nullish(),
  specializations: z
    .array(z.string())
    .nullish()
    .transform((v) => v ?? []),
  specialization: z.string().nullish(),
  bio: z.string().nullish(),
  profilePicture: z.string().nullish(),
  // Rich profile fields — kept nullish so the schema still parses against the
  // current `/sessions/practitioners` payload that doesn't yet return them.
  // They light up the practitioner card automatically once BE adds them to
  // the response (mirrors the admin `Practitioner.profile` shape).
  experienceYears: z.number().int().nullish(),
  qualification: z.string().nullish(),
  concerns: z
    .array(z.string())
    .nullish()
    .transform((v) => v ?? []),
  languages: z
    .array(z.string())
    .nullish()
    .transform((v) => v ?? []),
  // Singular role wrapper mirroring the admin `Practitioner.role` shape
  // (`{ id, role: 'psychologist' | 'volunteer' | ... } | null`). Stays
  // nullish because /sessions/practitioners may not include it yet — when
  // the BE adds it, the feedback card auto-shows the role label.
  role: z.object({ id: z.string(), role: z.string() }).nullish(),
});

export type AssignedPractitioner = z.infer<typeof assignedPractitionerSchema>;

type NameFields = Pick<AssignedPractitioner, 'name' | 'firstName' | 'lastName'>;
type SpecFields = Pick<AssignedPractitioner, 'specializations' | 'specialization'>;

/** Best-effort display name for a practitioner row in the picker. Accepts
 * any subset with name fields so call-sites can pass either the full record
 * from `/sessions/practitioners` or a narrow `{ name }` seeded from a session
 * detail response. */
export function practitionerDisplayName(p: NameFields): string {
  if (p.name?.trim()) return p.name;
  const parts = [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
  return parts || 'Practitioner';
}

/** Best-effort specialization line — falls back across two common BE shapes. */
export function practitionerSpecializationLine(p: SpecFields): string | null {
  if (p.specializations.length > 0) return p.specializations.join(' · ');
  if (p.specialization?.trim()) return p.specialization;
  return null;
}
